<template>
  <div class="header-wrapper">
    <header class="header">
      <nav class="navbar">
        <div class="navbar-container">
          <!-- 移动端汉堡菜单按钮 -->
          <button class="hamburger-btn" @click="toggleMobileMenu" v-if="isMobile">
            <span :class="{ 'active': mobileMenuOpen }"></span>
            <span :class="{ 'active': mobileMenuOpen }"></span>
            <span :class="{ 'active': mobileMenuOpen }"></span>
          </button>

          <!-- 左侧：Logo和导航 -->
          <div class="navbar-left">
            <router-link to="/" class="logo">
              <div class="logo-icon">
                <img src="@/assets/images/logo.png" alt="logo" class="logo-img" />
              </div>
              <span class="logo-text">{{ siteName }}</span>
            </router-link>

            <!-- 桌面端导航菜单 -->
            <div class="nav-menu" v-if="!isMobile">
              <router-link to="/" class="nav-item" exact>
                <span class="nav-text">首页</span>
                <span class="nav-line"></span>
              </router-link>
              <router-link to="/database" class="nav-item">
                <span class="nav-text">资料库</span>
                <span class="nav-line"></span>
              </router-link>
              <router-link to="/forum" class="nav-item" v-if="forumEnabled">
                <span class="nav-text">论坛</span>
                <span class="nav-line"></span>
              </router-link>
              <router-link to="/contact" class="nav-item">
                <span class="nav-text">联系我们</span>
                <span class="nav-line"></span>
              </router-link>
            </div>
          </div>

          <!-- 右侧：用户操作 -->
          <div class="navbar-right">
            <!-- 桌面端签到按钮 -->
            <div 
              class="nav-action sign-wrapper" 
              @mouseenter="handleSignMouseEnter" 
              @mouseleave="handleSignMouseLeave"
              v-if="!isMobile"
            >
              <button class="btn-sign" @click="handleSignClick">
                <span>签到</span>
              </button>

              <div 
                class="sign-popover" 
                v-show="showSignPopover && isLoggedIn" 
                @mouseenter="handlePopoverMouseEnter"
                @mouseleave="handlePopoverMouseLeave"
                @click.stop
              >
                <div class="sign-popover-inner">
                  <div class="sign-calendar">
                    <div class="calendar-header">
                      <span class="year-month">{{ currentYearMonth }}</span>
                    </div>
                    <div class="calendar-weekdays">
                      <span v-for="day in weekdays" :key="day">{{ day }}</span>
                    </div>
                    <div class="calendar-days">
                      <div 
                        v-for="(day, index) in calendarDays" 
                        :key="index"
                        class="calendar-day"
                        :class="{ 'empty': !day, 'signed': day && isSigned(day), 'today': day && isToday(day) }"
                      >
                        <template v-if="day">
                          <span class="day-num">{{ day }}</span>
                          <span class="sign-stamp" v-if="isSigned(day)">已签</span>
                        </template>
                      </div>
                    </div>
                  </div>
                  <div class="sign-rule"><p>每日签到可获得积分，连续签到奖励更丰厚</p></div>
                  <div class="sign-action">
                    <button class="sign-main-btn" :class="{ 'signed': todaySigned }" :disabled="todaySigned || signLoading" @click="handleSign">
                      <span v-if="signLoading">签到中...</span>
                      <span v-else-if="todaySigned">今日已签到</span>
                      <span v-else>签到</span>
                    </button>
                  </div>
                  <div class="sign-stats">
                    <span>连续签到 <strong>{{ continuousDays }}</strong> 天</span>
                    <span>累计签到 <strong>{{ totalSignDays }}</strong> 天</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 移动端签到按钮 -->
            <button class="btn-sign mobile-sign-btn" @click="handleSignClick" v-if="isMobile && isLoggedIn">
              <span>签到</span>
            </button>

            <!-- 未登录状态 -->
            <template v-if="!isLoggedIn">
              <template v-if="!isMobile">
                <div class="nav-action">
                  <router-link to="/register" class="btn-register">注册</router-link>
                </div>
                <div class="nav-action">
                  <router-link to="/login" class="btn-login">登录</router-link>
                </div>
              </template>
              <template v-else>
                <router-link to="/login" class="btn-login-mobile">登录</router-link>
              </template>
            </template>

            <!-- 已登录状态 -->
            <template v-else>
              <!-- 桌面端用户菜单 -->
              <div class="nav-action user-menu-wrapper" 
                   v-if="!isMobile"
                   @mouseenter="handleUserMenuEnter" 
                   @mouseleave="handleUserMenuLeave">
                <div class="user-info-trigger">
                  <img :src="userAvatar" class="user-avatar-small" @error="onAvatarError" />
                  <span class="welcome-text">欢迎，{{ username }}</span>
                  <span class="notify-dot" v-if="unreadCount > 0">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
                  <span class="arrow-down">▾</span>
                </div>
                <div class="user-dropdown" v-show="showUserMenu"
                     @mouseenter="handleUserMenuEnter"
                     @mouseleave="handleUserMenuLeave">
                  <div class="dropdown-follow-info">
                    <span @click="goTo('/user/following')">关注 <strong>{{ profileFollowingCount }}</strong></span>
                    <span @click="goTo('/user/followers')">粉丝 <strong>{{ profileFollowerCount }}</strong></span>
                  </div>
                  <div class="dropdown-divider"></div>
                  <router-link to="/user/notifications" class="dropdown-item">
                    通知 <span class="notify-badge" v-if="unreadCount > 0">{{ unreadCount }}</span>
                  </router-link>
                  <router-link to="/user/center" class="dropdown-item">个人中心</router-link>
                  <a href="javascript:void(0)" class="dropdown-item" @click="handleLogout">退出登录</a>
                </div>
              </div>
              
              <!-- 移动端用户头像 -->
              <div class="mobile-user-avatar" v-else @click="toggleMobileUserMenu">
                <img :src="userAvatar" class="user-avatar-small" @error="onAvatarError" />
                <span class="notify-dot" v-if="unreadCount > 0">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
              </div>
            </template>
          </div>
        </div>
      </nav>

      <!-- 移动端抽屉菜单 -->
      <transition name="fade">
        <div class="mobile-menu-overlay" v-if="mobileMenuOpen" @click.self="closeMobileMenu">
          <transition name="slide">
            <div class="mobile-menu-drawer" @click.stop>
              <div class="mobile-menu-header">
                <span class="mobile-menu-title">菜单</span>
                <button class="close-btn" @click="closeMobileMenu">✕</button>
              </div>
              <div class="mobile-menu-items">
                <router-link to="/" class="mobile-nav-item" @click="closeMobileMenu" :class="{ 'active': $route.path === '/' }">
                  <span class="mobile-nav-icon">🏠</span> 首页
                </router-link>
                <router-link to="/database" class="mobile-nav-item" @click="closeMobileMenu" :class="{ 'active': $route.path.startsWith('/database') }">
                  <span class="mobile-nav-icon">📚</span> 资料库
                </router-link>
                <router-link to="/forum" class="mobile-nav-item" v-if="forumEnabled" @click="closeMobileMenu" :class="{ 'active': $route.path.startsWith('/forum') }">
                  <span class="mobile-nav-icon">💬</span> 论坛
                </router-link>
                <router-link to="/contact" class="mobile-nav-item" @click="closeMobileMenu" :class="{ 'active': $route.path === '/contact' }">
                  <span class="mobile-nav-icon">📧</span> 联系我们
                </router-link>
                <div class="mobile-menu-divider"></div>
                <template v-if="!isLoggedIn">
                  <router-link to="/login" class="mobile-nav-item" @click="closeMobileMenu">
                    <span class="mobile-nav-icon">🔑</span> 登录
                  </router-link>
                  <router-link to="/register" class="mobile-nav-item" @click="closeMobileMenu">
                    <span class="mobile-nav-icon">📝</span> 注册
                  </router-link>
                </template>
                <template v-else>
                  <div class="mobile-user-info">
                    <img :src="userAvatar" class="mobile-user-avatar-large" @error="onAvatarError" />
                    <div class="mobile-user-details">
                      <div class="mobile-username">{{ username }}</div>
                      <div class="mobile-user-stats">
                        <span>关注 {{ profileFollowingCount }}</span>
                        <span>粉丝 {{ profileFollowerCount }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="mobile-menu-divider"></div>
                  <router-link to="/user/center" class="mobile-nav-item" @click="closeMobileMenu">
                    <span class="mobile-nav-icon">👤</span> 个人中心
                  </router-link>
                  <router-link to="/user/notifications" class="mobile-nav-item" @click="closeMobileMenu">
                    <span class="mobile-nav-icon">🔔</span> 通知 
                    <span class="notify-badge" v-if="unreadCount > 0">{{ unreadCount }}</span>
                  </router-link>
                  <a href="javascript:void(0)" class="mobile-nav-item" @click="handleLogout">
                    <span class="mobile-nav-icon">🚪</span> 退出登录
                  </a>
                </template>
              </div>
            </div>
          </transition>
        </div>
      </transition>
    </header>

    <!-- 移动端签到弹窗 - 完全独立于header -->
    <div 
      v-if="showSignPopover && isMobile && isLoggedIn" 
      class="mobile-sign-overlay"
      @click="closeMobileSign"
    >
      <div class="mobile-sign-dialog" @click.stop>
        <div class="mobile-sign-dialog-header">
          <span class="mobile-sign-dialog-title">每日签到</span>
          <button class="mobile-sign-dialog-close" @click="closeMobileSign">✕</button>
        </div>
        <div class="mobile-sign-dialog-body">
          <div class="mobile-sign-calendar">
            <div class="mobile-sign-calendar-header">
              <span class="mobile-sign-year-month">{{ currentYearMonth }}</span>
            </div>
            <div class="mobile-sign-calendar-weekdays">
              <span v-for="day in weekdays" :key="day">{{ day }}</span>
            </div>
            <div class="mobile-sign-calendar-days">
              <div 
                v-for="(day, index) in calendarDays" 
                :key="index"
                class="mobile-sign-calendar-day"
                :class="{ 'empty': !day, 'signed': day && isSigned(day), 'today': day && isToday(day) }"
              >
                <template v-if="day">
                  <span class="mobile-sign-day-num">{{ day }}</span>
                  <span class="mobile-sign-stamp" v-if="isSigned(day)">已签</span>
                </template>
              </div>
            </div>
          </div>
          <div class="mobile-sign-rule"><p>每日签到可获得积分，连续签到奖励更丰厚</p></div>
          <div class="mobile-sign-action">
            <button class="mobile-sign-main-btn" :class="{ 'signed': todaySigned }" :disabled="todaySigned || signLoading" @click="handleSign">
              <span v-if="signLoading">签到中...</span>
              <span v-else-if="todaySigned">今日已签到</span>
              <span v-else>签到</span>
            </button>
          </div>
          <div class="mobile-sign-stats">
            <span>连续签到 <strong>{{ continuousDays }}</strong> 天</span>
            <span>累计签到 <strong>{{ totalSignDays }}</strong> 天</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { userSign, getSignInfo, getProfile } from '@/api/user'
import { getUnreadCount } from '@/api/notification'
import { getForumConfig } from '@/api/forum'
import defaultAvatar from '@/assets/images/logo.png'

export default {
  name: 'Header',
  data() {
    return {
      showSignPopover: false,
      signLoading: false,
      todaySigned: false,
      continuousDays: 0,
      totalSignDays: 0,
      signedDates: [],
      weekdays: ['日', '一', '二', '三', '四', '五', '六'],
      currentDate: new Date(),
      closeTimer: null,
      openTimer: null,
      showUserMenu: false,
      userMenuTimer: null,
      profileFollowingCount: 0,
      profileFollowerCount: 0,
      unreadCount: 0,
      forumEnabled: true,
      defaultAvatar,
      isMobile: false,
      mobileMenuOpen: false,
      showMobileUserMenu: false
    }
  },
  computed: {
    ...mapState(['siteName']),
    ...mapGetters(['isLoggedIn', 'username', 'avatar']),

    userAvatar() {
      return this.avatar || this.defaultAvatar
    },

    currentYearMonth() {
      const year = this.currentDate.getFullYear()
      const month = this.currentDate.getMonth() + 1
      return `${year}年${month}月`
    },

    calendarDays() {
      const year = this.currentDate.getFullYear()
      const month = this.currentDate.getMonth()
      const firstDay = new Date(year, month, 1)
      const lastDay = new Date(year, month + 1, 0)
      const startDayOfWeek = firstDay.getDay()
      const daysInMonth = lastDay.getDate()
      const days = []
      for (let i = 0; i < startDayOfWeek; i++) { days.push(null) }
      for (let i = 1; i <= daysInMonth; i++) { days.push(i) }
      const remaining = 42 - days.length
      for (let i = 0; i < remaining; i++) { days.push(null) }
      return days
    }
  },
  watch: {
    isLoggedIn: {
        immediate: true,
        handler(val) {
            if (val) {
                this.$store.dispatch('fetchUserInfo')
                this.fetchSignInfo()
                this.fetchFollowCounts()
                this.fetchUnreadCount()
            }
        }
    },
    // 路由变化时关闭所有移动端弹层
    '$route'() {
        this.mobileMenuOpen = false
        this.showSignPopover = false
        this.showUserMenu = false
    }
},
  created() {
    this.checkForumEnabled()
    this.checkMobile()
  },
  mounted() {
    window.addEventListener('resize', this.checkMobile)
  },
  beforeDestroy() {
    this.clearTimers()
    if (this.userMenuTimer) clearTimeout(this.userMenuTimer)
    window.removeEventListener('resize', this.checkMobile)
  },
  methods: {
    ...mapActions(['logout']),

    clearTimers() {
      if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null }
      if (this.openTimer) { clearTimeout(this.openTimer); this.openTimer = null }
    },

    checkMobile() {
      this.isMobile = window.innerWidth <= 768
      if (!this.isMobile) {
        this.mobileMenuOpen = false
        this.showSignPopover = false
      }
    },

    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen
    },

    closeMobileMenu() {
      this.mobileMenuOpen = false
    },

    toggleMobileUserMenu() {
      this.showMobileUserMenu = !this.showMobileUserMenu
    },

    closeMobileSign() {
      this.showSignPopover = false
    },

    onAvatarError(e) {
      e.target.src = this.defaultAvatar
    },

    async checkForumEnabled() {
      try {
        const res = await getForumConfig()
        if (res.code === 200) this.forumEnabled = res.data.enabled !== false
      } catch (e) { /* ignore */ }
    },

    async fetchFollowCounts() {
      try {
        const res = await getProfile()
        if (res.code === 200) {
          this.profileFollowingCount = res.data.followingCount || 0
          this.profileFollowerCount = res.data.followerCount || 0
        }
      } catch (e) { /* ignore */ }
    },

    async fetchUnreadCount() {
      try {
        const res = await getUnreadCount()
        if (res.code === 200) this.unreadCount = res.data.count || 0
      } catch (e) { /* ignore */ }
    },

    goTo(path) {
      this.showUserMenu = false
      this.$router.push(path).catch(() => {})
    },

    // 签到相关
    handleSignMouseEnter() {
      if (this.isMobile) return
      this.clearTimers()
      if (!this.isLoggedIn) return
      this.openTimer = setTimeout(() => { this.showSignPopover = true; this.fetchSignInfo(); this.openTimer = null }, 100)
    },
    handleSignMouseLeave() {
      if (this.isMobile) return
      this.clearTimers()
      this.closeTimer = setTimeout(() => { this.showSignPopover = false; this.closeTimer = null }, 200)
    },
    handlePopoverMouseEnter() {
      if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null }
    },
    handlePopoverMouseLeave() {
      this.closeTimer = setTimeout(() => { this.showSignPopover = false; this.closeTimer = null }, 200)
    },
    async fetchSignInfo() {
      try {
        const res = await getSignInfo()
        if (res.code === 200) {
          const data = res.data
          this.todaySigned = data.todaySigned || false
          this.continuousDays = data.continuousDays || 0
          this.totalSignDays = data.totalSignDays || 0
          this.signedDates = data.signedDates || []
        }
      } catch (error) { console.error('获取签到信息失败:', error) }
    },
    isSigned(day) {
      if (!day) return false
      const year = this.currentDate.getFullYear()
      const month = String(this.currentDate.getMonth() + 1).padStart(2, '0')
      const dayStr = String(day).padStart(2, '0')
      const dateStr = `${year}-${month}-${dayStr}`
      return this.signedDates.includes(dateStr)
    },
    isToday(day) {
      if (!day) return false
      const today = new Date()
      return today.getFullYear() === this.currentDate.getFullYear() && 
             today.getMonth() === this.currentDate.getMonth() && 
             today.getDate() === day
    },
    handleSignClick() {
      if (!this.isLoggedIn) {
        this.$message.info('请先登录后再签到')
        this.$router.push('/login').catch(() => {})
        return
      }
      if (this.isMobile) {
        // 移动端打开弹窗
        this.showSignPopover = true
        this.fetchSignInfo()
      } else {
        // 桌面端
        if (!this.showSignPopover) {
          this.clearTimers()
          this.showSignPopover = true
          this.fetchSignInfo()
        }
      }
    },
    async handleSign() {
      if (!this.isLoggedIn) {
        this.$message.info('请先登录后再签到')
        this.$router.push('/login').catch(() => {})
        return
      }
      if (this.todaySigned) {
        this.$message.info('今日已签到')
        return
      }
      this.signLoading = true
      try {
        const res = await userSign()
        if (res.code === 200) {
          const data = res.data
          this.todaySigned = true
          this.continuousDays = data.continuousDays || this.continuousDays + 1
          this.totalSignDays += 1
          const today = new Date()
          const year = today.getFullYear()
          const month = String(today.getMonth() + 1).padStart(2, '0')
          const day = String(today.getDate()).padStart(2, '0')
          this.signedDates.push(`${year}-${month}-${day}`)
          this.$message.success(`签到成功！连续签到${this.continuousDays}天，获得${data.points}积分`)
          
          // 移动端签到成功后延迟关闭弹窗
          if (this.isMobile) {
            setTimeout(() => {
              this.showSignPopover = false
            }, 1500)
          }
        } else {
          this.$message.info(res.message || '签到失败')
        }
      } catch (error) {
        this.$message.error(error.message || '签到失败')
      } finally {
        this.signLoading = false
      }
    },

    // 用户菜单相关
    handleUserMenuEnter() {
      if (this.isMobile) return
      if (this.userMenuTimer) { clearTimeout(this.userMenuTimer); this.userMenuTimer = null }
      this.showUserMenu = true
      this.fetchFollowCounts()
      this.fetchUnreadCount()
    },
    handleUserMenuLeave() {
      if (this.isMobile) return
      this.userMenuTimer = setTimeout(() => { this.showUserMenu = false }, 500)
    },

    async handleLogout() {
      try {
        await this.logout()
        this.$message.success('已退出登录')
        this.showSignPopover = false
        this.showUserMenu = false
        this.mobileMenuOpen = false
        this.clearTimers()
        this.todaySigned = false
        this.continuousDays = 0
        this.totalSignDays = 0
        this.signedDates = []
        this.unreadCount = 0
        if (this.$route.path !== '/') {
          this.$router.push('/').catch(() => {})
        }
      } catch (error) {
        this.$message.error('退出失败')
      }
    }
  }
}
</script>

<style scoped>
/* ============ 桌面端样式（原有样式） ============ */
.header {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.05), 0 2px 8px rgba(0, 0, 0, 0.03);
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid rgba(230, 0, 18, 0.08);
}

.navbar-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 60px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 52px;
}

.navbar-left {
  display: flex;
  align-items: center;
  gap: 60px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  transition: all 0.3s ease;
}

.logo:hover {
  transform: translateY(-1px);
}

.logo-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.logo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.logo:hover .logo-icon {
  box-shadow: 0 6px 12px rgba(255, 71, 87, 0.3);
}

.logo-text {
  font-size: 18px;
  font-weight: 600;
  background: linear-gradient(135deg, #ff4757 0%, #ff6b81 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 0.5px;
}

.nav-menu {
  display: flex;
  align-items: center;
  gap: 12px;
}

.nav-item {
  font-size: 15px;
  font-weight: 500;
  color: #4a4a4a;
  text-decoration: none;
  padding: 6px 18px;
  border-radius: 24px;
  position: relative;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.nav-text {
  position: relative;
  z-index: 1;
}

.nav-line {
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 2px;
  background: linear-gradient(135deg, #ff4757 0%, #ff6b81 100%);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.nav-item:hover {
  color: #ff4757;
  background: rgba(255, 71, 87, 0.05);
}

.nav-item:hover .nav-line {
  width: 24px;
}

.nav-item.router-link-active {
  color: #ff4757;
  background: rgba(255, 71, 87, 0.08);
}

.nav-item.router-link-active .nav-line {
  width: 24px;
}

.navbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.nav-action {
  display: flex;
  align-items: center;
}

.sign-wrapper {
  position: relative;
}

.sign-wrapper::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  height: 20px;
  background: transparent;
}

.btn-sign {
  display: flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #ffd700 0%, #ffb347 100%);
  border: none;
  padding: 6px 20px;
  border-radius: 24px;
  font-size: 13px;
  font-weight: 600;
  color: #8b4513;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(255, 179, 71, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.btn-sign:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(255, 179, 71, 0.35);
  background: linear-gradient(135deg, #ffe44d 0%, #ffc067 100%);
}

.sign-popover {
  position: absolute;
  top: calc(100% + 12px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateX(-50%) translateY(-8px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}

.sign-popover::before {
  content: '';
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid #fff;
  filter: drop-shadow(0 -2px 4px rgba(0, 0, 0, 0.05));
}

.sign-popover-inner {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  padding: 20px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  width: 340px;
  box-sizing: border-box;
}

.sign-calendar {
  margin-bottom: 16px;
}

.calendar-header {
  text-align: center;
  margin-bottom: 16px;
}

.year-month {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 8px;
}

.calendar-weekdays span {
  text-align: center;
  font-size: 12px;
  color: #999;
  font-weight: 500;
  padding: 4px 0;
}

.calendar-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.calendar-day {
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 13px;
  color: #333;
  background: #f8f9fa;
  transition: all 0.2s ease;
  min-height: 36px;
  max-height: 40px;
  overflow: hidden;
  position: relative;
}

.calendar-day.empty {
  background: transparent;
}

.calendar-day.today {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  border: 1px solid #ffb347;
}

.calendar-day.signed {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
}

.day-num {
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
}

.sign-stamp {
  font-size: 9px;
  color: #4caf50;
  font-weight: 600;
  padding: 1px 3px;
  border-radius: 10px;
  background: rgba(76, 175, 80, 0.1);
  margin-top: 1px;
  line-height: 1;
  white-space: nowrap;
}

.calendar-day.signed .sign-stamp {
  color: #2e7d32;
}

.sign-rule {
  padding: 12px 0;
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 16px;
}

.sign-rule p {
  font-size: 12px;
  color: #888;
  margin: 0;
  line-height: 1.5;
}

.sign-action {
  margin-bottom: 12px;
}

.sign-main-btn {
  width: 100%;
  padding: 12px;
  background: linear-gradient(135deg, #ffd700 0%, #ffb347 100%);
  border: none;
  border-radius: 30px;
  font-size: 15px;
  font-weight: 600;
  color: #8b4513;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(255, 179, 71, 0.25);
}

.sign-main-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(255, 179, 71, 0.35);
}

.sign-main-btn.signed {
  background: linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%);
  color: #757575;
  box-shadow: none;
  cursor: default;
}

.sign-main-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.sign-stats {
  display: flex;
  justify-content: space-around;
  font-size: 13px;
  color: #666;
}

.sign-stats strong {
  color: #ff4757;
  font-size: 16px;
  margin-left: 4px;
}

.btn-register {
  padding: 6px 20px;
  border: 1.5px solid #ff4757;
  border-radius: 24px;
  font-size: 13px;
  font-weight: 600;
  color: #ff4757;
  text-decoration: none;
  transition: all 0.3s ease;
  background: transparent;
}

.btn-register:hover {
  background: rgba(255, 71, 87, 0.08);
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(255, 71, 87, 0.15);
}

.btn-login {
  padding: 6px 22px;
  background: linear-gradient(135deg, #ff4757 0%, #ff6b81 100%);
  border: none;
  border-radius: 24px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(255, 71, 87, 0.3);
}

.btn-login:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(255, 71, 87, 0.4);
  background: linear-gradient(135deg, #ff5a6e 0%, #ff7b94 100%);
}

.user-menu-wrapper {
  position: relative;
  cursor: pointer;
}

.user-info-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-avatar-small {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  background: #f5f5f5;
  border: 1px solid #eee;
}

.welcome-text {
  font-size: 13px;
  font-weight: 500;
  color: #6a6a6a;
}

.notify-dot {
  background: #e60012;
  color: #fff;
  font-size: 10px;
  padding: 1px 5px;
  border-radius: 10px;
  font-weight: 600;
}

.arrow-down {
  font-size: 10px;
  color: #999;
  margin-left: 2px;
}

.user-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  min-width: 160px;
  z-index: 200;
  padding: 8px 0;
}

.dropdown-follow-info {
  display: flex;
  justify-content: space-around;
  padding: 8px 0;
}

.dropdown-follow-info span {
  font-size: 13px;
  color: #888;
  cursor: pointer;
  text-align: center;
}

.dropdown-follow-info span:hover {
  color: #e60012;
}

.dropdown-follow-info strong {
  font-size: 16px;
  color: #333;
  display: block;
}

.dropdown-divider {
  height: 1px;
  background: #f0f0f0;
  margin: 4px 0;
}

.dropdown-item {
  display: block;
  padding: 8px 16px;
  font-size: 13px;
  color: #555;
  text-decoration: none;
  transition: all 0.2s;
  position: relative;
}

.dropdown-item:hover {
  background: #f5f5f5;
  color: #e60012;
}

.notify-badge {
  background: #e60012;
  color: #fff;
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 10px;
  margin-left: 6px;
}

/* ============ 移动端样式 ============ */
@media (max-width: 768px) {
  .navbar-container {
    padding: 0 16px;
    height: 56px;
    gap: 8px;
  }
  
  .hamburger-btn {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;
    width: 24px;
    height: 24px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    flex-shrink: 0;
  }
  
  .hamburger-btn span {
    display: block;
    width: 20px;
    height: 2px;
    background: #333;
    transition: all 0.3s;
    border-radius: 2px;
  }
  
  .hamburger-btn span.active:nth-child(1) {
    transform: translateY(6px) rotate(45deg);
  }
  
  .hamburger-btn span.active:nth-child(2) {
    opacity: 0;
  }
  
  .hamburger-btn span.active:nth-child(3) {
    transform: translateY(-6px) rotate(-45deg);
  }
  
  .navbar-left {
    gap: 8px;
    flex: 1;
    min-width: 0;
  }
  
  .logo-text {
    font-size: 15px;
  }
  
  .logo-icon {
    width: 28px;
    height: 28px;
  }
  
  .navbar-right {
    gap: 8px;
    flex-shrink: 0;
  }
  
  .mobile-sign-btn {
    padding: 4px 12px;
    font-size: 12px;
  }
  
  .btn-login-mobile {
    padding: 4px 12px;
    background: linear-gradient(135deg, #ff4757 0%, #ff6b81 100%);
    border: none;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 600;
    color: #fff;
    text-decoration: none;
  }
  
  .mobile-user-avatar {
    position: relative;
    cursor: pointer;
  }
  
  .mobile-user-avatar .user-avatar-small {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    object-fit: cover;
    border: 1px solid #eee;
    display: block;
  }
  
  .mobile-user-avatar .notify-dot {
    position: absolute;
    top: -2px;
    right: -4px;
    background: #e60012;
    color: #fff;
    font-size: 9px;
    padding: 1px 4px;
    border-radius: 8px;
    font-weight: 600;
  }
  
  /* 移动端抽屉菜单 */
  .mobile-menu-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
    overflow: hidden;
  }
  
  .mobile-menu-drawer {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: 80%;
    max-width: 300px;
    height: 100vh;
    background: #fff;
    z-index: 1000;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    display: flex;
    flex-direction: column;
  }
  
  .mobile-menu-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid #f0f0f0;
    flex-shrink: 0;
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 1;
  }
  
  .mobile-menu-title {
    font-size: 16px;
    font-weight: 600;
    color: #333;
  }
  
  .close-btn {
    background: none;
    border: none;
    font-size: 20px;
    color: #999;
    cursor: pointer;
    padding: 4px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background 0.2s;
    flex-shrink: 0;
  }
  
  .close-btn:hover {
    background: #f5f5f5;
  }
  
  .close-btn:active {
    background: #e8e8e8;
  }
  
  .mobile-menu-items {
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 8px 0;
  }
  
  .mobile-nav-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 20px;
    font-size: 15px;
    color: #333;
    text-decoration: none;
    transition: background 0.2s;
  }
  
  .mobile-nav-item:hover {
    background: #f5f5f5;
  }
  
  .mobile-nav-item.active {
    color: #ff4757;
    background: rgba(255, 71, 87, 0.05);
  }
  
  .mobile-nav-icon {
    font-size: 18px;
  }
  
  .mobile-menu-divider {
    height: 1px;
    background: #f0f0f0;
    margin: 8px 0;
  }
  
  .mobile-user-info {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
  }
  
  .mobile-user-avatar-large {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #f0f0f0;
  }
  
  .mobile-user-details {
    flex: 1;
  }
  
  .mobile-username {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin-bottom: 4px;
  }
  
  .mobile-user-stats {
    display: flex;
    gap: 12px;
    font-size: 12px;
    color: #999;
  }
  
  .notify-badge {
    background: #e60012;
    color: #fff;
    font-size: 10px;
    padding: 1px 6px;
    border-radius: 10px;
    margin-left: 6px;
  }
  
  /* 过渡动画 */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease;
  }
  
  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
  
  .slide-enter-active,
  .slide-leave-active {
    transition: transform 0.3s ease;
  }
  
  .slide-enter,
  .slide-leave-to {
    transform: translateX(-100%);
  }
}

/* ============ 移动端签到弹窗 - 独立样式 ============ */
.mobile-sign-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
}

.mobile-sign-dialog {
  width: 100%;
  max-width: 340px;
  max-height: 80vh;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;
}

.mobile-sign-dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
  background: #fff;
}

.mobile-sign-dialog-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.mobile-sign-dialog-close {
  background: none;
  border: none;
  font-size: 20px;
  color: #999;
  cursor: pointer;
  padding: 4px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s;
  flex-shrink: 0;
}

.mobile-sign-dialog-close:hover {
  background: #f5f5f5;
}

.mobile-sign-dialog-close:active {
  background: #e8e8e8;
}

.mobile-sign-dialog-body {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 16px;
  box-sizing: border-box;
}

.mobile-sign-calendar {
  margin-bottom: 12px;
}

.mobile-sign-calendar-header {
  text-align: center;
  margin-bottom: 12px;
}

.mobile-sign-year-month {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.mobile-sign-calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 3px;
  margin-bottom: 6px;
}

.mobile-sign-calendar-weekdays span {
  text-align: center;
  font-size: 11px;
  color: #999;
  font-weight: 500;
  padding: 3px 0;
}

.mobile-sign-calendar-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 3px;
}

.mobile-sign-calendar-day {
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 12px;
  color: #333;
  background: #f8f9fa;
  transition: all 0.2s ease;
  min-height: 32px;
  max-height: 36px;
  overflow: hidden;
  position: relative;
}

.mobile-sign-calendar-day.empty {
  background: transparent;
}

.mobile-sign-calendar-day.today {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  border: 1px solid #ffb347;
}

.mobile-sign-calendar-day.signed {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
}

.mobile-sign-day-num {
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
}

.mobile-sign-stamp {
  font-size: 8px;
  color: #4caf50;
  font-weight: 600;
  padding: 1px 2px;
  border-radius: 8px;
  background: rgba(76, 175, 80, 0.1);
  margin-top: 1px;
  line-height: 1;
  white-space: nowrap;
}

.mobile-sign-rule {
  padding: 10px 0;
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 12px;
}

.mobile-sign-rule p {
  font-size: 12px;
  color: #888;
  margin: 0;
  line-height: 1.5;
}

.mobile-sign-action {
  margin-bottom: 10px;
}

.mobile-sign-main-btn {
  width: 100%;
  padding: 10px;
  background: linear-gradient(135deg, #ffd700 0%, #ffb347 100%);
  border: none;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 600;
  color: #8b4513;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 3px 8px rgba(255, 179, 71, 0.25);
}

.mobile-sign-main-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 5px 12px rgba(255, 179, 71, 0.35);
}

.mobile-sign-main-btn.signed {
  background: linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%);
  color: #757575;
  box-shadow: none;
  cursor: default;
}

.mobile-sign-main-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.mobile-sign-stats {
  display: flex;
  justify-content: space-around;
  font-size: 12px;
  color: #666;
}

.mobile-sign-stats strong {
  color: #ff4757;
  font-size: 14px;
  margin-left: 3px;
}
</style>